import { legStatusTag } from "kontrolEdge/components/CarrierAccess/CarrierAccessDetails/carrierAccessLegs";
import type { CarrierAccessLeg } from "kontrolEdge/components/CarrierAccess/carrierAccess.types";
import { priorityLabel, sortLegsByPriority } from "./carrierAccess";

/**
 * The legs of the dual Backhaul behind a Port or an Internet, with the status
 * the backend read for each on its own router (CTX-1133). The Port's and the
 * service's status already count them; this is what their tooltips spell out,
 * in the same words as the Backhaul detail ("Standby" is not a warning).
 */
export interface BackhaulLegStatus {
  carrierCode: string;
  priority: string | null;
  status: CarrierAccessLeg["status"];
  statusMessage: string | null;
}

/** The legs a response carries, or none — a Port without a dual Backhaul. */
export function backhaulLegsOf(
  ...sources: Array<{ backhaulLegs?: BackhaulLegStatus[] } | null | undefined>
): BackhaulLegStatus[] {
  for (const source of sources) {
    const legs = source?.backhaulLegs;
    if (Array.isArray(legs) && legs.length > 0) return legs;
  }
  return [];
}

/** "IENTC (Primary): Standby · MCM (Secondary): Active". No interface names. */
export function backhaulLegsSummary(legs: readonly BackhaulLegStatus[]): string | null {
  if (legs.length === 0) return null;
  return sortLegsByPriority(legs)
    .map((leg) => {
      const priority = priorityLabel(leg.priority);
      const name = priority ? `${leg.carrierCode} (${priority})` : leg.carrierCode;
      return `${name}: ${legStatusTag(leg.status).label}`;
    })
    .join(" · ");
}

/**
 * A status row's tooltip with the legs appended: the reason first, when
 * there is one, then each leg. Undefined when there is nothing to say.
 */
export function withBackhaulLegsTooltip(
  message: string | null | undefined,
  legs: readonly BackhaulLegStatus[]
): string | undefined {
  const parts = [message, backhaulLegsSummary(legs)].filter(
    (part): part is string => Boolean(part)
  );
  return parts.length > 0 ? parts.join(". ") : undefined;
}
